import { defineStore } from 'pinia'
import { useCudaApi } from '~/composables/api/useCuda'
import { type PaymentProfile, usePaymentApi } from '~/composables/api/usePayment'

export const usePaymentProfileStore = defineStore('payment-profile', () => {
  const appStore = useAppStore()
  const locationStore = useLocationStore()
  const { getAcceptedTnCs } = useCudaApi()
  const { getPaymentProfileSummary } = usePaymentApi()

  // ---- State
  const acceptedLegalEntitiesTnC = ref<number[] | null>([])
  const paymentProfileError = ref<boolean>(false)
  const paymentProfileLoaded = ref<boolean>(false)
  const paymentProfiles = ref<Array<PaymentProfile>>([])
  const selectedPaymentProfile = ref<PaymentProfile | null>(null)

  // ---- Computed
  const hasAcceptedTnCForCurrentLegalEntity = computed<boolean>(() => {
    if (acceptedLegalEntitiesTnC.value !== null) {
      return acceptedLegalEntitiesTnC.value.includes(locationStore.city.legalEntityId)
    }
    else {
      return false
    }
  })

  // ---- Actions
  async function fetchAcceptedTncs() {
    const response = await getAcceptedTnCs(appStore.accessToken)
    if (response !== null) {
      acceptedLegalEntitiesTnC.value = [...response.body.acceptedLegalEntityTerms]
    }
    else {
      acceptedLegalEntitiesTnC.value = null
    }
  }

  async function fetchPaymentProfiles() {
    const response = await getPaymentProfileSummary(useAppStore().accessToken)
    if (response === null) {
      paymentProfileError.value = true
      return
    }
    const loadedPaymentProfiles: PaymentProfile[] = response.body.paymentProfiles.map(profile => ({ ...profile }))
    if (loadedPaymentProfiles.length === 1) {
      selectedPaymentProfile.value = loadedPaymentProfiles[0]
    }
    else {
      const defaultProfile = loadedPaymentProfiles.find(profile => profile.defaultProfile)
      if (defaultProfile) {
        selectedPaymentProfile.value = defaultProfile
      }
    }
    paymentProfiles.value = loadedPaymentProfiles
    paymentProfileLoaded.value = true
  }

  function setSelectedPaymentProfile(profile: PaymentProfile) {
    selectedPaymentProfile.value = profile
  }

  return {
    hasAcceptedTnCForCurrentLegalEntity,
    fetchAcceptedTncs,
    fetchPaymentProfiles,
    acceptedLegalEntitiesTnC,
    paymentProfileLoaded,
    paymentProfileError,
    paymentProfiles,
    selectedPaymentProfile,
    setSelectedPaymentProfile,
  }
})
